import {motion} from 'framer-motion'
import {useEffect} from 'react'

import {twMerge} from '@sdox/utils'

import type {ReactNode} from 'react'
import type {HTMLMotionProps} from 'framer-motion'

export interface OverlayProps extends Omit<HTMLMotionProps<'div'>, 'children'> {
  visible: boolean
  absolute?: boolean
  onClose(): void
  onTop?: boolean
  closeOnClick?: boolean
  children?: ReactNode
}

export const Overlay = ({
  visible,
  className,
  children,
  onClose,
  absolute,
  onTop = false,
  closeOnClick = true,
  ...restProps
}: OverlayProps) => {
  useEffect(() => {
    if (visible) {
      const handleKeyUp = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          onClose()
        }
      }

      document.addEventListener('keyup', handleKeyUp)

      return () => {
        document.removeEventListener('keyup', handleKeyUp)
      }
    }
  }, [visible, onClose])

  return (
    <motion.div
      initial={'hidden'}
      animate={visible ? 'visible' : 'hidden'}
      variants={{visible: {opacity: 1, display: 'flex'}, hidden: {opacity: 0, transitionEnd: {display: 'none'}}}}
      className={twMerge(
        'inset-0 flex-col items-center justify-center bg-black/80',
        absolute ? 'absolute' : 'fixed',
        closeOnClick && 'cursor-pointer',
        onTop ? 'z-50' : 'z-30',
        className,
      )}
      onClick={() => {
        if (closeOnClick) {
          onClose()
        }
      }}
      {...restProps}
    >
      {visible ? (
        <div
          onClick={(e) => {
            e.stopPropagation()
            e.nativeEvent.stopImmediatePropagation()
          }}
          className={'relative'}
        >
          {children}
        </div>
      ) : null}
    </motion.div>
  )
}
